import { useMemo } from 'react'

import { NetworkStatus } from '@apollo/client'
import type { DeepPartial } from '@apollo/client/utilities'
import { useQuery } from '@apollo/client/react'
import type { useQuery as UseQuery } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { buildTagComparator } from '~publish/components/FilterByTag'
import { PostCard_Post } from '~publish/components/PostCard/fragments/PostCard_Post'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { debugLog } from '~publish/helpers/debugLog'
import type { FilterablePostType } from '~publish/helpers/postType'
import { CalendarPostCard_Post } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'
import type { UseIntersectionObserverReturn } from '~publish/hooks/useIntersectionObserver'

import { useRealTimePusherUpdates } from './useRealTimePusherUpdates'
import { PostNoteTotal } from '~publish/hooks/notes/fragments/PostNoteTotal'
import { PostUnreadMentionCount } from '~publish/hooks/notes/fragments/PostUnreadMentionCount'
import { PostUnreadNoteCount } from '~publish/hooks/notes/fragments/PostUnreadNoteCount'
import { useNoteCountVariables } from '~publish/hooks/notes/useNoteCountVariables'
import type { NoteCountSelection } from '~publish/hooks/notes/useNoteCountVariables'
import { isOptionalNotesSummaryError } from '~publish/hooks/notes/isOptionalNotesSummaryError'

export const POSTS_PER_PAGE = 20
export const MAX_POSTS_REFETCH_LIMIT = 300

export const GetPostList = graphql(
  `
    query GetPostList(
      $first: Int!
      $after: String
      $organizationId: OrganizationId!
      $channelIds: [ChannelId!]
      $tags: TagComparator
      $status: [PostStatus!]
      $sort: [PostSortInput!]
      $dueAtPresence: DateTimePresence
      $dueAt: DateTimeComparator
      $createdAt: DateTimeComparator
      $postTypes: [PostType!]
      $includeUnreadNoteCount: Boolean! = false
      $includeNotesButtonCounts: Boolean! = false
    ) {
      posts(
        input: {
          organizationId: $organizationId
          filter: {
            channelIds: $channelIds
            tags: $tags
            status: $status
            dueAtPresence: $dueAtPresence
            dueAt: $dueAt
            createdAt: $createdAt
            postTypes: $postTypes
          }
          sort: $sort
        }
        first: $first
        after: $after
      ) {
        edges {
          node {
            id
            dueAt
            sentAt
            ...PostCard_Post
            ...CalendarPostCard_Post
            ...PostUnreadNoteCount @include(if: $includeUnreadNoteCount)
            ...PostUnreadMentionCount @include(if: $includeNotesButtonCounts)
            ...PostNoteTotal @include(if: $includeNotesButtonCounts)
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
      }
    }
  `,
  [
    PostCard_Post,
    CalendarPostCard_Post,
    PostUnreadNoteCount,
    PostUnreadMentionCount,
    PostNoteTotal,
  ],
)

type GetPostListVariables = VariablesOf<typeof GetPostList>

export type PostStatus = NonNullable<GetPostListVariables['status']>[number]
export type PostSortInput = NonNullable<GetPostListVariables['sort']>[number]
export type DateTimePresence = NonNullable<
  GetPostListVariables['dueAtPresence']
>
export type DateTimeComparator = NonNullable<GetPostListVariables['dueAt']>

export type SortPreset = 'upcomingFirst' | 'mostRecentlyPostedFirst'

type UsePaginatedPostListBaseOptions = {
  status: PostStatus[]
  channelIds?: string[]
  tagIds?: string[]
  /** Post formats to filter by. Omit or pass an empty list for every format. */
  postTypes?: readonly FilterablePostType[]
  sortPreset?: SortPreset
  perPage?: number
  dueAtPresence?: DateTimePresence
  dueAt?: DateTimeComparator
  createdAt?: DateTimeComparator
  /** Skips the query entirely — e.g. a column that can never have any posts. */
  skip?: boolean
  /** The Note counts this list renders. Defaults to none. */
  noteCounts?: NoteCountSelection
}

export type UsePaginatedPostListOptions = UsePaginatedPostListBaseOptions & {
  returnPartialData?: false
}

type UsePaginatedPostListPartialOptions = UsePaginatedPostListBaseOptions & {
  returnPartialData: true
}

type GetPostListData = ResultOf<typeof GetPostList>
type PartialGetPostListData = DeepPartial<GetPostListData>
type PostListDataResult = GetPostListData | PartialGetPostListData

type UsePaginatedPostListResult<TData extends PostListDataResult> = {
  data: TData | undefined
  loading: boolean
  fetchingMore: boolean
  /** A filter/sort change swapped in new variables the cache has no entry for yet — `data` goes briefly undefined. */
  reloading: boolean
  error: UseQuery.Result<GetPostListData, GetPostListVariables>['error']
  lastElementRef: UseIntersectionObserverReturn[0]
  refetch: UseQuery.Result<GetPostListData, GetPostListVariables>['refetch']
}

const mapSortPresetToSortOrder = (sortPreset: SortPreset): PostSortInput[] => {
  switch (sortPreset) {
    case 'upcomingFirst':
      return [
        { field: 'dueAt', direction: 'asc' },
        { field: 'createdAt', direction: 'desc' },
      ]
    case 'mostRecentlyPostedFirst':
      return [
        { field: 'dueAt', direction: 'desc' },
        { field: 'createdAt', direction: 'desc' },
      ]
  }
}

export function usePaginatedPostList(
  options: UsePaginatedPostListPartialOptions,
): UsePaginatedPostListResult<PartialGetPostListData>
export function usePaginatedPostList(
  options: UsePaginatedPostListOptions,
): UsePaginatedPostListResult<GetPostListData>
export function usePaginatedPostList({
  status,
  channelIds,
  tagIds,
  postTypes,
  sortPreset = 'upcomingFirst',
  perPage = POSTS_PER_PAGE,
  dueAtPresence,
  dueAt,
  createdAt,
  skip = false,
  returnPartialData = false,
  noteCounts = 'none',
}:
  | UsePaginatedPostListOptions
  | UsePaginatedPostListPartialOptions): UsePaginatedPostListResult<PostListDataResult> {
  const organizationId = useOrganizationId()
  const noteCountVariables = useNoteCountVariables(noteCounts)
  const { data, refetch, fetchMore, error, networkStatus, variables } =
    useQuery(GetPostList, {
      variables: {
        first: perPage,
        after: null,
        organizationId: organizationId || '',
        ...noteCountVariables,
        // An empty array is not "no status filter" on the wire — the
        // backend maps it to a `status: { $in: [] }` filter, matching
        // nothing. Omit it entirely so an unset status filter (e.g. a
        // Board column with no active Status filter) fetches every status.
        status: status.length ? status : undefined,
        channelIds,
        tags: buildTagComparator(tagIds),
        sort: mapSortPresetToSortOrder(sortPreset),
        ...(dueAtPresence ? { dueAtPresence } : {}),
        ...(dueAt ? { dueAt } : {}),
        ...(createdAt ? { createdAt } : {}),
        ...(postTypes?.length ? { postTypes: [...postTypes] } : {}),
      },
      fetchPolicy: 'cache-and-network',
      // cache-first (not cache-only) so infinite-scroll `fetchMore` can reach
      // the network; cache-only still serves cached pages without re-fetching.
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
      returnPartialData,
      skip: !organizationId || skip,
    })

  // Data-first: a background validation can report loading/setVariables even
  // when the cache already has data for these variables — gate on both, or
  // cached content flashes a skeleton for no reason.
  const loading = networkStatus === NetworkStatus.loading && !data?.posts
  const fetchingMore = networkStatus === NetworkStatus.fetchMore
  const reloading = networkStatus === NetworkStatus.setVariables && !data?.posts
  const postError =
    data?.posts && isOptionalNotesSummaryError(error) ? undefined : error

  if (networkStatus === NetworkStatus.refetch) {
    debugLog('🔄 refetching posts...')
  }

  useRealTimePusherUpdates({
    filters: {
      status,
      tagIds,
      channelIds,
      dueAtPresence,
      postTypes,
    },
    variables,
    refetch: async (options): Promise<void> => {
      debugLog('⚡ posts refetch triggered', options?.mode)

      const loadedPostCount = data?.posts?.edges?.length ?? 0
      const insertDelta =
        options?.mode === 'insert' ? Math.max(1, options.insertCount ?? 1) : 0

      await refetch({
        first:
          // HACK: fetch as many more posts as were inserted in case of insert
          // mode this works up to a limit, but it helps resolving issue of
          // new posts not showing up
          loadedPostCount > 0
            ? // HACK: we currently have very high limits on the number of posts we can fetch
              // so we need to limit the number of posts we fetch to avoid overwhelming the server
              Math.min(loadedPostCount + insertDelta, MAX_POSTS_REFETCH_LIMIT)
            : POSTS_PER_PAGE,
      })
    },
    isPostVisible: (id: string): boolean => {
      return data?.posts?.edges
        ? data.posts.edges.some((edge) => edge?.node?.id === id)
        : false
    },
    sort: sortPreset,
  })

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: fetchingMore,
    hasNextPage: data?.posts?.pageInfo?.hasNextPage,
    fetchMore: async () => {
      try {
        await fetchMore({
          variables: {
            after: data?.posts?.pageInfo?.endCursor,
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult?.posts?.edges) return prev

            const newEdges = fetchMoreResult.posts.edges
            // Create a Set of new post IDs for O(1) lookup
            const newPostIds = new Set(newEdges.map((edge) => edge.node.id))

            // Filter existing posts not in new results - O(n) operation
            const existingEdges = (prev.posts?.edges || []).filter(
              (edge) => edge.node && !newPostIds.has(edge.node.id),
            )

            // Combine existing and new edges - O(n + m) operation
            const updatedEdges = [...existingEdges, ...newEdges]

            return {
              ...prev,
              posts: {
                ...prev.posts,
                edges: updatedEdges,
                pageInfo: fetchMoreResult.posts.pageInfo,
              },
            }
          },
        })
      } catch (caughtError) {
        const message =
          caughtError instanceof Error ? caughtError.message : 'Unknown error'

        toast.error('Error fetching more posts')
        throw new Error(`Failed to fetch more posts: ${message}`, {
          cause: caughtError,
        })
      }
    },
  })

  return useMemo(
    () => ({
      data,
      loading,
      fetchingMore,
      reloading,
      error: postError,
      lastElementRef,
      refetch,
    }),
    [
      data,
      loading,
      fetchingMore,
      reloading,
      postError,
      lastElementRef,
      refetch,
    ],
  )
}
